import { chunk } from 'lodash';

import { leaderboardClient } from '@neptune/shared/core-apis-leaderboard-domain';
import { ShortIdsByProject } from '@neptune/shared/entity-domain';
import { makeEntityIdentifier } from '@neptune/shared/entity-util';

import { Attribute, AttributeModelConverter } from 'domain/experiment/attribute';

type QueryAttributesRequest = {
  limit?: number;
  fields: string[];
  nextPageToken?: string;
  /** temporary workaround to include last for preview series, remove once new ingestion model is deployed */
  includeLastForPreviewSeries?: boolean;
  /** array of **qualified id** or run **UUID** that uniquely identifies run in the entire system (cross project and workspaces)  */
  experimentIdsFilter?: string[];
};

export type QueryAttributesResult = {
  shortId: string;
  entityId: string;
  projectName: string;
  organizationName: string;
  attributes: Attribute[];
  projectIdentifier: string;
};

type QueryAttributesResponse = {
  nextPageToken?: string;
  result: QueryAttributesResult[];
};

export async function queryAttributes({
  limit,
  nextPageToken,
  experimentIdsFilter,
  includeLastForPreviewSeries,
  fields: attributeNamesFilter,
}: QueryAttributesRequest): Promise<QueryAttributesResponse> {
  const nextPage =
    limit === undefined && nextPageToken === undefined ? undefined : { limit, nextPageToken };

  const result = await leaderboardClient.queryAttributesWithinProject({
    query: {
      nextPage,
      experimentIdsFilter,
      attributeNamesFilter,
      ...(includeLastForPreviewSeries ? { includeLastForPreviewSeries } : {}),
    },
  });

  return {
    nextPageToken: result.nextPage.nextPageToken,
    result: result.entries.map(
      ({
        attributes,
        experimentShortId: shortId,
        experimentId: entityId,
        organizationName,
        projectName,
      }) => ({
        shortId,
        entityId,
        projectName,
        organizationName,
        projectIdentifier: makeEntityIdentifier(organizationName, projectName),
        attributes: attributes.map(AttributeModelConverter.attributeFromApiToDomain),
      }),
    ),
  };
}

type QueryAllAttributesRequestBase = {
  fields: string[];
  /** temporary workaround to include last for preview series, remove once new ingestion model is deployed */
  includeLastForPreviewSeries?: boolean;
};

type QueryAllAttributesRequestMultiProject = QueryAllAttributesRequestBase & {
  shortIdsByProject: ShortIdsByProject;
  projectIdentifier?: never;
  shortIds?: never;
};

type QueryAllAttributesRequest = QueryAllAttributesRequestMultiProject;

const MAX_CELL_DATA_TO_FETCH_IN_SINGLE_REQUEST = 10_000;

/**
 * Fetches all attributes for given fields and shortIds. Bare endpoint returns up to
 * `MAX_CELL_DATA_TO_FETCH_IN_SINGLE_REQUEST` (10_000) cells per request. This function handles
 * pagination and returns all cells for given fields and shortIds.
 */
export async function queryFieldsFromAllPages(
  params: QueryAllAttributesRequestMultiProject,
): Promise<QueryAttributesResponse['result']>;

export async function queryFieldsFromAllPages({
  fields,
  shortIdsByProject,
  includeLastForPreviewSeries,
}: QueryAllAttributesRequest): Promise<QueryAttributesResponse['result']> {
  const experimentIdsFilter = Array.from(shortIdsByProject.entries()).flatMap(
    ([project, shortIds]) => shortIds.map((shortId) => makeEntityIdentifier(project, shortId)),
  );
  const deduplicatedExperimentIdsFilter = Array.from(new Set(experimentIdsFilter));

  const shortIdChunkSize = Math.floor(MAX_CELL_DATA_TO_FETCH_IN_SINGLE_REQUEST / fields.length);
  const totalChunks = Math.ceil(deduplicatedExperimentIdsFilter.length / shortIdChunkSize);
  const amortizedChunkSize = Math.ceil(deduplicatedExperimentIdsFilter.length / totalChunks);

  const result = await Promise.all(
    chunk(deduplicatedExperimentIdsFilter, amortizedChunkSize).map((chunk) =>
      queryAttributes({ fields, experimentIdsFilter: chunk, includeLastForPreviewSeries }),
    ),
  );

  return result.flatMap(({ result: pageResult }) => pageResult);
}

/**
 * Fetches all attributes for given fields and uuids. Bare endpoint returns up to
 * `MAX_CELL_DATA_TO_FETCH_IN_SINGLE_REQUEST` (10_000) cells per request. This function handles
 * pagination and returns all cells for given fields and shortIds.
 */
export async function queryFieldsFromAllPagesByUuid({
  fields,
  uuids,
  includeLastForPreviewSeries,
}: QueryAllAttributesRequestBase & {
  uuids: string[];
}): Promise<QueryAttributesResponse['result']> {
  const deduplicatedUuids = Array.from(new Set(uuids));
  const shortIdChunkSize = Math.floor(MAX_CELL_DATA_TO_FETCH_IN_SINGLE_REQUEST / fields.length);
  const totalChunks = Math.ceil(deduplicatedUuids.length / shortIdChunkSize);
  const amortizedChunkSize = Math.ceil(deduplicatedUuids.length / totalChunks);

  const result = await Promise.all(
    chunk(deduplicatedUuids, amortizedChunkSize).map((chunk) =>
      queryAttributes({ fields, experimentIdsFilter: chunk, includeLastForPreviewSeries }),
    ),
  );

  return result.flatMap(({ result: pageResult }) => pageResult);
}
